/**
 * ExitIntentModal — capture d'abandon au départ (desktop).
 *
 * Propose une dernière action à forte valeur (simulation / diagnostic) routée
 * par l'audience effective. Desktop-only via useExitIntent. Une fois par session.
 *
 * Expérience A/B `exp_exit_intent` : actif par défaut (fallback 'exit_modal'),
 * désactivable pour un groupe témoin (variante 'control').
 *
 * À monter sur le layout 'classic' uniquement (pas pendant un funnel).
 */

import { useEffect, useRef } from 'react';
import { ArrowRight, X, Sparkles } from 'lucide-react';
import { useExitIntent } from '../../hooks/useExitIntent';
import { useEffectiveAudience } from '../../hooks/useEffectiveAudience';
import { useInProgressJourney } from '../../hooks/useInProgressJourney';
import { AUDIENCE_NAV } from '../../config/audience-nav';
import { ROUTES } from '@shared/constants/routes';
import type { AudienceId } from '@shared/constants/audiences';
import { useExperiment, captureEvent, trackExperimentConversion } from '../../lib/analytics';
import { EXPERIMENTS } from '../../config/experiments';
import { Button } from '../ui/button';
import { TrustBar } from './TrustBar';

const EXIT_BODY: Partial<Record<AudienceId, string>> = {
  particuliers:
    "En 2 minutes, estimez MaPrimeRénov', les primes CEE et le reste à charge de votre projet. Gratuit, sans engagement.",
  professionnels:
    "En 60 secondes, estimez vos primes CEE et vos économies d'énergie à partir de votre SIRET. Gratuit, sans engagement.",
  entreprises:
    'En 60 secondes, chiffrez vos primes CEE multi-sites et votre plan Décret Tertiaire. Gratuit, sans engagement.',
  collectivites:
    'Estimez les primes CEE et le plan −40 % pour votre patrimoine public. Gratuit, sans engagement.',
  coproprietes:
    "Estimez MaPrimeRénov' Copropriétés et les primes CEE collectives pour votre AG. Gratuit, sans engagement.",
  artisans:
    "Estimez la prime CEE de votre prochain chantier — 0 € d'avance, paiement sous 30 à 60 jours.",
  agriculteurs:
    'Estimez vos primes CEE agricoles (AGRI) et les aides cumulables pour votre exploitation. Gratuit.',
};

const DEFAULT_BODY =
  "En 60 secondes, découvrez les aides et primes auxquelles vous êtes éligible. C'est gratuit, sans engagement et sans carte bancaire.";

export function ExitIntentModal() {
  const dialogRef = useRef<HTMLDivElement | null>(null);
  const closeRef = useRef<HTMLButtonElement | null>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);
  const { variant } = useExperiment<'exit_modal' | 'control'>(
    EXPERIMENTS.exitIntentOffer.key,
    'exit_modal',
  );
  const journey = useInProgressJourney();
  const { effective } = useEffectiveAudience();
  // On n'arme pas si un parcours est en cours (la bannière de reprise gère ce cas)
  // ni pour le groupe témoin.
  const enabled = variant !== 'control' && !journey;
  const { triggered, reset } = useExitIntent({ enabled });

  useEffect(() => {
    if (triggered) {
      captureEvent('exit_intent_shown', { audience: effective?.id ?? 'none' });
    }
  }, [triggered, effective]);

  // Focus modal : entrée explicite, boucle Tab, fermeture Échap et restauration.
  useEffect(() => {
    if (!triggered || !enabled) return;
    previousFocusRef.current =
      document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    closeRef.current?.focus();

    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        reset();
        return;
      }
      if (event.key !== 'Tab' || !dialogRef.current) return;
      const focusable = Array.from(
        dialogRef.current.querySelectorAll<HTMLElement>(
          'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])',
        ),
      ).filter((el) => !el.hasAttribute('disabled') && el.tabIndex !== -1);
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (!first || !last) return;
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = previousOverflow;
      if (previousFocusRef.current?.isConnected) {
        previousFocusRef.current.focus();
      }
    };
  }, [enabled, reset, triggered]);

  if (!triggered || !enabled) return null;

  const cta = effective
    ? AUDIENCE_NAV[effective.id].primaryCta
    : { label: 'Découvrir nos outils gratuits', href: ROUTES.outils };

  const body = effective ? (EXIT_BODY[effective.id] ?? DEFAULT_BODY) : DEFAULT_BODY;

  const onAccept = () => {
    captureEvent('exit_intent_accept', { audience: effective?.id ?? 'none', href: cta.href });
    trackExperimentConversion(EXPERIMENTS.exitIntentOffer.key, variant, 'funnel_start', {
      audience: effective?.id ?? 'none',
    });
  };

  return (
    <div className="fixed inset-0 z-[60] hidden items-center justify-center p-4 lg:flex">
      {/* Backdrop cliquable — vrai bouton pour clavier + a11y */}
      <button
        type="button"
        aria-label="Fermer la fenêtre"
        onClick={reset}
        className="absolute inset-0 h-full w-full cursor-default bg-ink/40 backdrop-blur-sm"
      />
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="exit-intent-title"
        className="relative w-full max-w-md rounded-3xl border border-stone-200 bg-white p-7 shadow-[0_24px_80px_rgba(10,10,10,.28)]"
      >
        <button
          ref={closeRef}
          type="button"
          onClick={reset}
          aria-label="Fermer"
          className="absolute right-4 top-4 flex h-9 w-9 items-center justify-center rounded-lg text-stone-400 transition-colors hover:bg-stone-100 hover:text-stone-700"
        >
          <X className="h-4 w-4" />
        </button>

        <span className="inline-flex h-11 w-11 items-center justify-center rounded-2xl bg-brand-100 text-brand-700">
          <Sparkles className="h-5 w-5" strokeWidth={2} />
        </span>

        <h2 id="exit-intent-title" className="h-section mt-4">
          Avant de partir…
        </h2>
        <p className="mt-2 text-[15px] leading-relaxed text-stone-600">{body}</p>

        <Button asChild variant="brand" size="xl" className="mt-5 w-full rounded-xl">
          <a href={cta.href} onClick={onAccept}>
            {cta.label}
            <ArrowRight className="h-4 w-4" strokeWidth={2.2} />
          </a>
        </Button>

        <button
          type="button"
          onClick={reset}
          className="mt-3 w-full text-center text-[13px] text-stone-400 transition-colors hover:text-stone-600"
        >
          Non merci, je continue ma visite
        </button>

        <div className="mt-5 border-t border-stone-100 pt-4">
          <TrustBar max={3} />
        </div>
      </div>
    </div>
  );
}
